import React, { memo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import L from 'leaflet';
import icons from './utils/icons';
import './leaflet.scss';
import './leaflet.draw.scss';

function FullScreenButtonControl({ map }: { map: L.Map }) {
  const { t } = useTranslation();

  useEffect(() => {
    let btn: HTMLButtonElement | null = null;
    let isFullscreen = false;
    function updateButtonIcon() {
      if (!btn) return;
      btn.innerHTML = isFullscreen ? icons.zoomOutIcon : icons.zoomInIcon;
    }

    const CustomButton = L.Control.extend({
      onAdd() {
        btn = L.DomUtil.create('button', 'leaflet-bar-fullscreen');
        btn.title = t('full-screen');
        updateButtonIcon();

        L.DomEvent.on(btn, 'click', (e) => {
          L.DomEvent.stopPropagation(e);
          L.DomEvent.preventDefault(e);

          const container = map.getContainer();
          const fullscreenElement =
            document.fullscreenElement ||
            (document as any).webkitFullscreenElement ||
            (document as any).mozFullScreenElement ||
            (document as any).msFullscreenElement;

          if (fullscreenElement) {
            if (document.exitFullscreen) {
              document.exitFullscreen();
            } else if ((document as any).webkitExitFullscreen) {
              (document as any).webkitExitFullscreen();
            } else if ((document as any).mozCancelFullScreen) {
              (document as any).mozCancelFullScreen();
            } else if ((document as any).msExitFullscreen) {
              (document as any).msExitFullscreen();
            }
          } else if (container.requestFullscreen) {
            container.requestFullscreen();
          } else if ((container as any).webkitRequestFullscreen) {
            (container as any).webkitRequestFullscreen();
          } else if ((container as any).mozRequestFullScreen) {
            (container as any).mozRequestFullScreen();
          } else if ((container as any).msRequestFullscreen) {
            (container as any).msRequestFullscreen();
          }
        });

        return btn;
      },
      onRemove() {
        return undefined;
      },
    });

    const control = new CustomButton({ position: 'topleft' });
    control.addTo(map);

    const controlContainer = map
      .getContainer()
      .querySelector('.leaflet-top.leaflet-left');
    const btnEl = control.getContainer();
    if (controlContainer && btnEl) {
      // Fullscreen button should be first
      controlContainer.insertBefore(btnEl, controlContainer.firstChild);
    }

    function handleChange() {
      const fullscreenElement =
        document.fullscreenElement ||
        (document as any).webkitFullscreenElement ||
        (document as any).mozFullScreenElement ||
        (document as any).msFullscreenElement;
      isFullscreen = !!fullscreenElement;
      updateButtonIcon();
    }

    document.addEventListener('fullscreenchange', handleChange);
    document.addEventListener('webkitfullscreenchange', handleChange);
    document.addEventListener('mozfullscreenchange', handleChange);
    document.addEventListener('MSFullscreenChange', handleChange);

    return () => {
      control.remove();
      document.removeEventListener('fullscreenchange', handleChange);
      document.removeEventListener('webkitfullscreenchange', handleChange);
      document.removeEventListener('mozfullscreenchange', handleChange);
      document.removeEventListener('MSFullscreenChange', handleChange);
    };
  }, [map, t]);

  return null;
}

export default memo(FullScreenButtonControl);
